import { Injectable } from '@angular/core';
import { AppConfig, sleep } from '@tytapp/common';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { UserService } from 'user';
import { UsersApi, ProductionsApi, LiveStreamsApi } from '@tytapp/api';
import { Podcast, VOD, Production, LiveStream } from '@tytapp/models';

import { PlayerComponent } from './player/player.component';
import { PlaybackSession, PlayState, PlaylistType, Playback } from './playback';
import { PlayerService } from './player/player.service';
import { Router } from '@angular/router';
import { LiveStreamsService } from 'common-ui';

export const CAST_NAMESPACE_TYT = 'urn:x-cast:com.tyt.cast';

export interface TYTCastMediaLoadData {
    type : string;
}

export interface TYTCastLiveMediaLoadData {
    type : "live";
    liveStreamID : number;
}

export interface TYTCastVODMediaLoadData {
    type : "vod";
    productionID : string;
    playlistID : PlaylistType;
    playlistItemID : string;
}

export interface TYTCastMessage {
    type : string;
}

export interface TYTCastMediaChangedMessage extends TYTCastMessage {
    type : "mediaChanged";
    media : TYTCastMediaLoadData;
}

export interface TYTCastLoginMessage extends TYTCastMessage {
    type : "login";
    token : string;
}

export class CastMediaPlayer implements cast.receiver.media.Player {

    constructor(
        private castService : GoogleCastService,
        private productionsApi : ProductionsApi,
        private liveStreamsApi : LiveStreamsApi,
        private player : PlayerComponent
    ) {
    }
    
    manager : cast.receiver.MediaManager;

    editTracksInfo(data: cast.receiver.media.TracksInfo) {
      // NOOP
    }
  
    currentTimeSec : number = 0;
    durationSec : number;
    tytPlaybackState : PlayState;

    getCurrentTimeSec(): number {
        return this.currentTimeSec;
    }
  
    getDurationSec(): number {
        return this.durationSec;
    }
  
    getState(): string {
        if (this.tytPlaybackState == "buffering")
            return "BUFFERING";
        else if (this.tytPlaybackState == "paused")
            return "PAUSED";
        else if (this.tytPlaybackState == "playing")
            return "PLAYING";
        else if (this.tytPlaybackState == "unstarted")
            return "BUFFERING";
        else if (this.playbackSession)
            return "BUFFERING";
        
        return "IDLE";
    }

    muted : boolean;
    volume : number;
  
    getVolume(): cast.receiver.media.Volume {
        let volume = new cast.receiver.media.Volume();
        volume.level = this.volume || 1;
        volume.muted = this.muted || false;
    
        return volume;
    }
  
    pause() {
        console.log(`GoogleCast: Received pause event`);
        if (!this.playbackSession)
            return;
        this.playbackSession.pause();
        this.manager.broadcastStatus(true);
    }
  
    play() {
        console.log(`GoogleCast: Received play event`);
        if (!this.playbackSession)
            return;
        this.playbackSession.resume();
        this.manager.broadcastStatus(true);
    }
  
    endedCallback : Function;
    errorCallback : Function;
    loadedCallback : Function;

    registerEndedCallback(endedCallback: any) {
        this.endedCallback = endedCallback;
    }
  
    registerErrorCallback(errorCallback: any) {
        this.errorCallback = errorCallback;
    }
  
    registerLoadCallback(loadCallback: any) {
        this.loadedCallback = loadCallback;
    }
  
    reset() {
        if (!this.playbackSession)
            return;
        this.playbackSession.destroy();
        this.playbackSession = null;
    }
  
    seek(time: number, resumeState?: string) {
        if (!this.playbackSession)
            return;

        console.log(`GoogleCast: Received seek event: ${time} / ${resumeState}`);
        this.playbackSession.seek(time);
        this.manager.broadcastStatus(true);
    }
  
    setVolume(volume: cast.receiver.media.Volume) {
        console.log(`GoogleCast: Received setVolume event:`);
        console.dir(volume);

        if (!this.playbackSession)
            return;

        this.playbackSession.setVolume(volume.level);
    }
  
    unregisterEndedCallback() {
        this.endedCallback = null;
    }
  
    unregisterErrorCallback() {
        this.errorCallback = null;
    }
  
    unregisterLoadCallback() {
        this.loadedCallback = null;
    }
  
    mediaInformation : cast.receiver.media.MediaInformation;

    async load(contentId, autoplay, time?, tracksInfo?, onlyLoadTracks?) {

        this.castService.loadingNewMedia = true;

        await this.castService.pendingLogin;

        let loadData : TYTCastMediaLoadData = JSON.parse(atob(contentId));

        console.log(`GoogleCast: Received media load request:`);
        console.dir({
            loadData,
            autoplay,
            time,
            tracksInfo,
            onlyLoadTracks
        });


        this.castService.paywalled = false;
        let user = this.castService.user;

        if (loadData.type === 'live') {
            let liveLoadData = <TYTCastLiveMediaLoadData> loadData;
            let liveStream = <LiveStream> await this.liveStreamsApi.get(liveLoadData.liveStreamID).toPromise();

            this.castService.mediaIsLive = true;
            if (liveStream.active_live_event) {
                if (liveStream.active_live_event.use_title && liveStream.active_live_event.title)
                    this.castService.mediaTitle = liveStream.active_live_event.title;
                else
                    this.castService.mediaTitle = undefined;

                if (liveStream.active_live_event.show)
                    this.castService.mediaShowName = liveStream.active_live_event.show.name;
                else
                    this.castService.mediaShowName = undefined;

                if (liveStream.active_live_event && liveStream.active_live_event.show)
                    this.castService.mediaThumbnail = liveStream.active_live_event.show.thumbnail_landscape1;

            } else {
                this.castService.mediaTitle = liveStream.title;
            }

            if (liveStream.premium && (!user || !user.entitled)) {                
                this.castService.paywalled = true;
                return;
            }

            let session = await this.playStream(liveStream);
            await session.resume();
        } else if (loadData.type === 'vod') {
            let vodLoadData = <TYTCastVODMediaLoadData> loadData;
            console.log(`CastMediaPlayer: Fetching production ${vodLoadData.productionID}...`);
            let production = await this.productionsApi.get(vodLoadData.productionID).toPromise();
            console.log(`CastMediaPlayer: Successfully loaded production ${vodLoadData.productionID}`);

            console.dir(production);

            let playlist : (VOD | Podcast)[] = production.full_length_vods;

            if (vodLoadData.playlistID == 'clips')
                playlist = production.vod_clips;
            else if (vodLoadData.playlistID == 'podcasts')
                playlist = production.full_length_podcasts;

            let vod = playlist[0];
            if (vodLoadData.playlistItemID)
                vod = playlist.find(x => x.id == vodLoadData.playlistItemID);

            if (vod) {
                this.castService.mediaIsLive = false;
                this.castService.mediaThumbnail = production.thumbnail_landscape1;
                this.castService.mediaTitle = vod.description || vod.title;
                this.castService.mediaShowName = production.show.name;
                
                console.log(`GoogleCast: Checking entitlement status for VOD:`);
                console.dir(vod);

                if (vod.is_premium && (!user || !user.entitled)) {
                    this.castService.paywalled = true;
                    return;
                }
                let session = await this.playItem(production, vod, vodLoadData.playlistID, playlist, time || 0);
                await session.resume();
            }
        }

        this.castService.loadingNewMedia = false;

        // Emit an event telling all senders that we are watching new media

        this.castService.broadcastMediaChange(loadData);


    }
  
    public attachToSession(player : PlayerComponent, session : PlaybackSession) {
        if (!session) {
            console.error(`GoogleCast/CastMediaPlayer: Cannot attach to null session!`);
            return;
        }
        
        this.mediaInformation = new cast.receiver.media.MediaInformation();
        this.mediaInformation.streamType = 'BUFFERED';
        this.mediaInformation.contentType = 'tyt';

        // Prepare a LoadData

        let loadData : TYTCastMediaLoadData;

        if (session.item.type === 'stream') {

            let liveStream : LiveStream = <LiveStream>session.item.item;

            if (liveStream.active_live_event && liveStream.active_live_event.show) {
                let meta = new cast.receiver.media.GenericMediaMetadata();
                let liveEvent = liveStream.active_live_event;

                if (liveEvent.use_title) {
                    meta.title = liveStream.active_live_event.title;
                    meta.subtitle = liveStream.active_live_event.show.name;
                } else {
                    meta.title = liveStream.active_live_event.show.name;
                }

                meta.releaseDate = new Date(liveEvent.air_time_unix).toISOString();
                meta.images = [
                    new cast.receiver.media.Image(liveEvent.show.logo_square)
                ];
            }

            this.castService.broadcastMediaChange(loadData = <TYTCastLiveMediaLoadData>{
                type: 'live',
                liveStreamID: (player.media as LiveStream).id as any
            });
        } else {

            if (this.production && this.production.show) {
                let meta = new cast.receiver.media.GenericMediaMetadata();
                let airDate = new Date(this.production.date_simple);
                let dateStr = `${airDate.getMonth()}/${airDate.getDate()}/${airDate.getFullYear()}`;

                meta.title = `${this.production.show.name}, ${dateStr}`;
                meta.subtitle = this.currentItem.description;
                meta.releaseDate = new Date(this.production.date_simple).toISOString();
                meta.images = [
                    new cast.receiver.media.Image(this.production.thumbnail_landscape1 || this.production.show.logo_square)
                ];
    
                this.mediaInformation.metadata = meta;
            }

            let playlistItem = player.media;
            let playlistID = player.playlistID;
            let production = player.production;
    
            this.castService.broadcastMediaChange(loadData = <TYTCastVODMediaLoadData>{
                type: 'vod',
                playlistID,
                productionID: production.id,
                playlistItemID: playlistItem.id
            });
        }

        this.mediaInformation.contentId = btoa(JSON.stringify(loadData));

        this.manager.setMediaInformation(this.mediaInformation);
        
        // Useful for debugging.
        window['tytPlaybackSession'] = session;
        window['castMediaPlayer'] = this;

        this.playbackSession = session;
        
        this.durationSec = session.length;
        this.currentTimeSec = session.position;

        let reportedLoad = false;

        this.playbackSession.finished.subscribe(() => {
            //if (this.endedCallback)
            //    this.endedCallback();
            this.manager.broadcastStatus(true);
        });

        this.playbackSession.playStateChanged.subscribe(state => {
            this.tytPlaybackState = state;
            this.manager.broadcastStatus(true);
        });

        this.playbackSession.positionChanged.subscribe(position => {
            this.currentTimeSec = position;
            this.durationSec = session.length;
            this.manager.broadcastStatus(true);
        });

        this.playbackSession.lengthChanged.subscribe(length => {
            this.durationSec = length;
            this.mediaInformation.duration = length;
            //this.manager.setMediaInformation(this.mediaInformation, true);

            if (length !== undefined && length !== null && !isNaN(length)) {
                if (this.loadedCallback && !reportedLoad) {
                    this.loadedCallback();
                    reportedLoad = true;
                }
            }

        })

        this.playbackSession.volumeChanged.subscribe(volume => {
            this.volume = volume;
            this.muted = false; // TODO
            this.manager.broadcastStatus(false);
        });

        this.manager.sendLoadComplete();
    }

    production : Production;
    playlist : (Podcast | VOD)[];
    playlistID : string;
    currentItem : VOD | Podcast | LiveStream;
    
    playbackSession : PlaybackSession;

    async playStream(liveStream : LiveStream) {
        this.production = null;
        this.playlistID = null;
        this.playlist = [];
        this.currentItem = liveStream;

        await sleep(1);

        return await this.player.playStream(liveStream);
    }

    async playItem(production : Production, vod : VOD | Podcast, playlistID : PlaylistType, playlist : (Podcast | VOD)[], position? : number): Promise<PlaybackSession> {
        
        this.production = production;
        this.playlistID = playlistID;
        this.playlist = playlist;
        this.currentItem = vod;

        await sleep(1);
    
        if (this.playlistID == 'podcasts')
          return await this.player.playPodcast(this.production, vod, playlistID, playlist, position);
        else
          return await this.player.playVOD(this.production, vod, playlistID, playlist, position); 
      }  
  }
  
@Injectable()
export class GoogleCastService {

    castContext: cast.framework.CastContext;

    private _availableChanged : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _connectedChanged : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _connectingChanged : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    private _stateChanged : Subject<cast.framework.CastStateEventData> = new Subject<cast.framework.CastStateEventData>();

    constructor(
        private appConfig: AppConfig,
        private playerService : PlayerService,
        private playbackService : Playback,
        private userService: UserService,
        private userApi : UsersApi,
        private productionsApi : ProductionsApi,
        private liveStreamsApi : LiveStreamsApi,
        private liveStreamsService : LiveStreamsService,
        private router : Router
    ) {

    }

    public get user() {
        return this.userService.user;
    }

    public get available() {
        return typeof cast !== 'undefined';
    }

    initialize() {
        console.log(`GoogleCast: initializing...`);
        this.castContext = cast.framework.CastContext.getInstance();
        this.castContext.setOptions({
            autoJoinPolicy: chrome.cast.AutoJoinPolicy.TAB_AND_ORIGIN_SCOPED,
            receiverApplicationId: this.appConfig.googleCastAppId
        });

        this.castContext.addEventListener(cast.framework.CastContextEventType.SESSION_STATE_CHANGED, async e => {
            if (e.sessionState == cast.framework.SessionState.SESSION_RESUMED || e.sessionState == cast.framework.SessionState.SESSION_STARTED) {
                this.allowResumeOnEnd = false;
                this.playbackService.enableSessionTracking = false;
                this.playbackService.lockControlsVisible = true;

                let session = e.session;
                let resuming = e.sessionState == cast.framework.SessionState.SESSION_RESUMED;

                if (resuming) {
                    setTimeout(async () => {
                        if (!this.currentSession)
                            return;
                        
                        let media = this.currentSession.getMediaSession();
                        if (!media)
                            return;
                        
                        if (!this.playbackService.currentPlayer || !this.playbackService.currentPlayer.getCurrentPlaybackSession()) {
                            let loadRequest = <TYTCastMediaLoadData> JSON.parse(btoa(media.media.contentId));
                            if (loadRequest.type === 'live') {
                                let liveLoad = <TYTCastLiveMediaLoadData> loadRequest;
                                let liveStream = await this.liveStreamsService.get(parseInt(''+liveLoad.liveStreamID));

                                this.playbackService.appPlayer.playAsset(null, null, [], liveStream, liveStream.asset, 'stream');
                            } else if (loadRequest.type === 'vod') {
                                let vodLoad = <TYTCastVODMediaLoadData> loadRequest;

                                let production = await this.productionsApi.get(vodLoad.productionID).toPromise();
                                let playlist : any[] = [];

                                if (vodLoad.playlistID === 'episodes')
                                    playlist = production.full_length_vods;
                                else if (vodLoad.playlistID === 'clips')
                                    playlist = production.vod_clips;
                                else if (vodLoad.playlistID === 'podcasts')
                                    playlist = production.full_length_podcasts;

                                let item = playlist.find(x => x.id === vodLoad.playlistItemID);

                                this.playbackService.appPlayer.playAsset(
                                    production, 
                                    vodLoad.playlistID, playlist, 
                                    item, 
                                    item.asset, 
                                    vodLoad.playlistID === 'podcasts' ? 'audio' : 'video'
                                );
                            }
                        }
                    }, 1000);
                }
                session.addMessageListener(CAST_NAMESPACE_TYT, async (ns, rawMessage) => {
                    console.log(`GoogleCast: Received event (${ns}):`);
                    let message : TYTCastMessage = JSON.parse(rawMessage);

                    if (message.type === 'mediaChanged') {
                        let mediaChangedMessage = <TYTCastMediaChangedMessage>message;
                        let media : TYTCastMediaLoadData = mediaChangedMessage.media;

                        console.log(`GoogleCast: Current media has changed to:`);
                        console.dir(media);

                        if (!this.playbackService.isAppPlayerActive && this.playbackService.currentPlayer) {
                            let session = this.playbackService.currentPlayer.getCurrentPlaybackSession();

                            if (session['isCastRemoteSession'] && session['isCurrentMedia']) {
                                if (media.type === 'live') {
                                    let liveMedia = <TYTCastLiveMediaLoadData>media;
        
                                    console.log(`Navigating to new live stream page:`);
                                    this.router.navigate([`/live/streams/${liveMedia.liveStreamID}`]);
        
                                } else if (media.type === 'vod') {
                                    let vodMedia = <TYTCastVODMediaLoadData>media;
                                    let production = await this.productionsApi.get(vodMedia.productionID).toPromise();
        
                                    console.log(`Navigating to new media page:`);
                                    this.router.navigate([`/watch/${production.show.slug || production.show.id}/${production.id}/${vodMedia.playlistID}/${vodMedia.playlistItemID}`]);
                                }
                            }
                        }

                    }

                    console.dir(message);
                });

                if (resuming)
                    console.log(`GoogleCast: Cast session resuming`);
                else
                    console.log(`GoogleCast: Cast session started`);

                if (session.getMediaSession()) {
                    console.log(`GoogleCast: Media is already playing back.`);
                } else {
                    console.log(`GoogleCast: No initial media session.`);
                }

                let autoplayOnRemote = !resuming;

                if (this.playbackService.currentPlayer) {
                    let session = this.playbackService.currentPlayer.getCurrentPlaybackSession();

                    if (session && !session['isGoogleCastSession']) {
                        // Take it over!
                        console.log(`GoogleCast: Switching to GoogleCast remote playback session...`);

                        let newSession = await this.playbackService.currentPlayer.refreshMediaResolution(!resuming);
                        console.log(`GoogleCast: Re-resolved playback session to:`);
                        console.dir(newSession);
                    }
                }

                session.addEventListener(cast.framework.SessionEventType.MEDIA_SESSION, async e => {
                    if (this.playbackService.currentPlayer) {
                        let session = this.playbackService.currentPlayer.getCurrentPlaybackSession();
                        let existingMediaId = undefined;

                        // TODO: this could be causing playstate jitter
                        if (session.item.type === 'stream') {
                            existingMediaId = `live:${session.item.item.id}`;
                        } else {
                            existingMediaId = `vod:${session.item.item.id}`;
                        }
                        
                        if (session['isGoogleCastSession'] && session[''])
                        console.log(`GoogleCast: Refreshing GoogleCast remote playback session for the new media session...`);

                        let refreshAutoplay = autoplayOnRemote;
                        autoplayOnRemote = false;

                        let newSession = await this.playbackService.currentPlayer.refreshMediaResolution(refreshAutoplay);
                    }
                });
            } else if (e.sessionState == cast.framework.SessionState.SESSION_ENDED || e.sessionState == cast.framework.SessionState.SESSION_ENDING) {
                
                this.playbackService.enableSessionTracking = true;
                this.playbackService.lockControlsVisible = false;
            }
        });

        this.castContext.addEventListener(cast.framework.CastContextEventType.CAST_STATE_CHANGED, async e => {
            console.log(`GoogleCast: State changed: ${e.castState}`);

            if (e.castState == "NO_DEVICES_AVAILABLE") {
                this._availableChanged.next(false);
                this._connectingChanged.next(false);
                this._connectedChanged.next(false);
                this._connected = false;
            } else if (e.castState == "NOT_CONNECTED") {
                this._availableChanged.next(true);
                this._connectingChanged.next(false);
                this._connectedChanged.next(false);
                this._connected = false;
                this.playbackService.globalBuffering = false;
            } else if (e.castState == "CONNECTING") {
                this._availableChanged.next(true);
                this._connectingChanged.next(true);
                this._connectedChanged.next(false);
                this._connected = true;
                this.playbackService.globalBuffering = true;

                let player = this.playbackService.currentPlayer;
                if (player) {
                    let session = player.getCurrentPlaybackSession();
                    if (session)
                        session.pause();
                }

            } else if (e.castState == "CONNECTED") {
                this._availableChanged.next(true);
                this._connectingChanged.next(false);
                this._connectedChanged.next(true);
                this._connected = true;
                this.playbackService.globalBuffering = false;
            }

            this._stateChanged.next(e);

            if (!this._connected && this.playbackService.currentPlayer) {

                // Sleep to allow the cast session to fully end before transitioning back.
                await sleep(1000);

                let session = this.playbackService.currentPlayer.getCurrentPlaybackSession();

                if (session['isGoogleCastSession']) {
                    // Take it back!

                    console.log(`GoogleCast: Switching from GoogleCast playback session back to regular playback...`);

                    let isPlaying = !['paused', 'unstarted'].includes(session.playState);
                    let newSession = await this.playbackService.currentPlayer.refreshMediaResolution(isPlaying && this.allowResumeOnEnd);

                    console.log(`GoogleCast: Re-resolved playback session to:`);
                    console.dir(newSession);
                }
            }
        });
    }

    async sendLoginInfo() {
        try {
            await this.castContext.getCurrentSession().sendMessage(CAST_NAMESPACE_TYT, <TYTCastLoginMessage>{
                type: 'login',
                token: this.userService.token
            })
        } catch (e) {
            console.error(`Chromecast: Caught error while sending login info`);
            console.error(e);
            throw e;
        }
    }

    async playLiveStream(liveStreamID : number) {
        await this.sendLoginInfo();

        let loadData : TYTCastLiveMediaLoadData = {
            type: "live",
            liveStreamID
        }

        try {
            await this.loadMedia(loadData, 0);
        } catch (e) {
            console.error(`While trying to load livestream media (liveStreamID=${liveStreamID}) onto Chromecast:`);
            console.error(e);
            throw e;
        }
        
        console.log('Load success');
    }

    async playVOD(productionID : string, playlistID : PlaylistType, playlistItemID : string, position : number) {

        console.log("Sending login info to Chromecast...");
        this.sendLoginInfo();
        console.log("Successfully sent login info.");

        let loadData : TYTCastVODMediaLoadData = {
            type: "vod",
            productionID, playlistID, playlistItemID
        };

        try {
            await this.loadMedia(loadData, position);
        } catch (e) {
            console.error(`While trying to load VOD media (production=${productionID}, playlistID=${playlistID}, playlistItemID=${playlistItemID}) onto Chromecast:`);
            console.error(e);
            throw e;
        }
        
        console.log('Load success');
    }

    async loadMedia(loadData : TYTCastMediaLoadData, position : number) {
        let mediaInfo = new chrome.cast.media.MediaInfo(btoa(JSON.stringify(loadData)), 'video/mp4');

        if (loadData.type === 'live')
            mediaInfo.streamType = <any>'LIVE';

        let request = new chrome.cast.media.LoadRequest(mediaInfo);
        request.currentTime = position;

        console.log(`GoogleCast: loadMedia():`);
        console.dir({
            loadData,
            position
        });
        console.log(`GoogleCast: loadMedia(): Request:`);
        console.dir(request);

        await this.castContext.getCurrentSession().loadMedia(request);
    }

    async castNow() {
        try {
            await this.castContext.requestSession();
        } catch (e) {
            console.log(`Failed to request session:`);
            console.log(e);
        }
    }

    /**
     * Set to true when the user explicitly ends the 
     * session. When true, the media will autoplay upon
     * resuming local playback.
     */
    allowResumeOnEnd = false;

    async stopCasting() {
        this.allowResumeOnEnd = true;
        let castSession = cast.framework.CastContext.getInstance().getCurrentSession();
        castSession.endSession(true);
    }

    get currentSession() {
        if (typeof cast === 'undefined')
            return null;
        if (!cast.framework || !cast.framework.CastContext || !cast.framework.CastContext.getInstance)
            return null;

        let context = cast.framework.CastContext.getInstance();
        
        if (context == null)
            return null;

        return context.getCurrentSession();
    }

    _connected : boolean = false;

    get connected(): boolean {
        return this._connected;
    }

    get stateChanged() : Observable<cast.framework.CastStateEventData> {
         return this._stateChanged;
    }

    get connectingChanged() : Observable<boolean> {
        return this._connectingChanged;
    }

    get availableChanged(): Observable<boolean> {
        return this._availableChanged;
    }

    get connectedChanged(): Observable<boolean> {
        return this._connectedChanged;
    }
    
    // load on receiver component ngOnInit()

    public get isReceiver() {
        if (typeof window === 'undefined')
            return false;
        
        return window.location.pathname.includes('/receiver');
    }

    mediaTitle : string = null;
    mediaShowName : string = null;
    mediaThumbnail : string = null;
    mediaIsLive : boolean = false;

    loadingNewMedia : boolean = false;
    _paywalled : boolean = false;

    get paywalled() {
        return this._paywalled;
    }

    set paywalled(value : boolean) {
        if (value && this._paywalled != value) {
            if (this.manager) {
                let receiverManager = cast.receiver.CastReceiverManager.getInstance();

                if (receiverManager) {
                    let caps = receiverManager.getDeviceCapabilities();

                    if (!caps.display_supported) {

                        // TODO: this doesn't seem to work on Cast devices.
                        // The alternative is to have the message recorded

                        if (typeof SpeechSynthesisUtterance !== 'undefined') {
                            let message = '';
                            if (this.user && this.user.membership && !this.user.membership.cancelled_at)
                                message = 'This content requires TYT membership to listen. Please visit tyt.com/account to update your billing information.';
                            else if (this.user)
                                message = 'This content requires TYT membership to listen. Visit tyt.com/join to become a member.';
                            else
                                message = 'This content requires TYT membership to listen. Login or Visit tyt.com/join to become a member';
                            var msg = new SpeechSynthesisUtterance(message);
                            window.speechSynthesis.speak(msg);
                        }
                    }
                }
            }
        }
        this._paywalled = value;
    }
    pendingLogin : Promise<void> = Promise.resolve();
    manager : cast.receiver.MediaManager;
    player : CastMediaPlayer;

    async initializeReceiver(player : PlayerComponent) {
        if (typeof window === 'undefined')
            return;

        window['isGoogleCastReceiver'] = true;
        
        await new Promise<void>((resolve, reject) => {
            let script = document.createElement('script');
            script.src = "//www.gstatic.com/cast/sdk/libs/receiver/2.0.0/cast_receiver.js";
            script.onload = function() {
                resolve();
            };
            document.body.appendChild(script);
        });

        let castPlayer = new CastMediaPlayer(this, this.productionsApi, this.liveStreamsApi, player);
        let manager = new cast.receiver.MediaManager(castPlayer, cast.receiver.media.Command.ALL_BASIC_MEDIA);
        this.manager = manager;
        this.player = castPlayer;
        castPlayer.manager = manager;

        let castReceiver = cast.receiver.CastReceiverManager.getInstance();
        
        console.dir(castReceiver);
    
        this.tytMessageBus = castReceiver.getCastMessageBus(CAST_NAMESPACE_TYT, 'JSON');
        this.tytMessageBus.onMessage = async event => {
            let message : TYTCastMessage = event.data;
            if (message.type == 'login') {
                let loginMessage : TYTCastLoginMessage = <TYTCastLoginMessage>message;

                if (loginMessage.token) {
                    let resolve : Function, reject : Function;
                    this.pendingLogin = new Promise((res, rej) => (resolve = res, reject = rej));

                    try {
                        await this.userService.assume(loginMessage.token);
                    } catch (e) {
                        console.error(`Caught exception while assuming user:`);
                        console.error(e);
                    }

                    console.log(`USER:`);
                    console.dir(this.userService.user);

                    resolve();
                } else {
                    this.userService.completeLogout();
                }
            }
        };

        try {
            castReceiver.start();
            castReceiver.setInactivityTimeout(10);
        } catch (e) {
            console.error(`Caught error while starting cast receiver:`);
            console.error(e);
        }
    }

    tytMessageBus : cast.receiver.CastMessageBus;

    initialized : boolean = false;
    initializeSender() {
        if (this.initialized)
            return;

        if (typeof window === 'undefined')
            return;

        if (window.location.pathname === '/cast/receiver') {
            console.warn("Refusing to load Cast Sender framework when running as Receiver.");
            return;
        }

        this.initialized = true;

        // if (this.isReceiver)
        //     return;

        let self = this;
        window['__onGCastApiAvailable'] = (isAvailable) => {
            if (isAvailable) {
                self.initialize();
            } else {
                console.log('(I) Google Cast: Not available');
            }
        };

        let script = document.createElement('script');
        script.src = "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
        document.body.appendChild(script);
    }

    broadcastMediaChange(media : TYTCastMediaLoadData) {
        this.manager.broadcastStatus(true);
        this.tytMessageBus.broadcast(<TYTCastMessage>{
            type: 'mediaChanged',
            media
        })
    }
}